import { ReactElement } from 'react';
import { Link, useRoutes } from 'react-router-dom';
import { CreateScene, DeleteScene, EntityScene, ModelScene, IndexScene, UpdateScene, useEntity } from 'components';

import permissions from 'modules/supplier/vendor/permissions';
import { formatDate } from 'utilities';
import { LoadingContainer, TableColumns } from '@ff-it/ui';
import type { Vendor, ListPriceList } from 'modules/supplier/vendor/types';
import useSWR from 'swr';
import { Outlet } from 'modules/supplier/outletOrServiceCategory/types';
import { PriceListForm } from 'modules/supplier/vendor/PriceListForm';

const columns: TableColumns<ListPriceList> = [
  {
    content: 'Period',
    key: 'date_from',
    sortable: true,
    render: ({ id, date_from, date_to }: ListPriceList) => (
      <Link to={`${id}`}>
        {formatDate(date_from)} - {formatDate(date_to)}
      </Link>
    ),
  },
];

const initialValues = {
  date_from: null,
  date_to: null,
  prices: {},
};

export function PriceLists(): ReactElement {
  const {
    item: { id },
    endpoint,
  } = useEntity<Vendor>();

  const { data, isLoading } = useSWR<Outlet[]>({
    url: `${endpoint}outlets/`,
    method: 'GET',
  });

  return (
    <LoadingContainer loading={isLoading}>
      <ModelScene
        endpoint={`supplier/vendors/${id}/price_lists/`}
        title="Price list"
        pluralTitle="Price lists"
        entityTitle={({ date_from, date_to }: ListPriceList) => `${formatDate(date_from)} - ${formatDate(date_to)}`}
        permissions={permissions}
      >
        {data && <PriceListRoutes outlets={data} />}
      </ModelScene>
    </LoadingContainer>
  );
}

function PriceListRoutes({ outlets }: { outlets: Outlet[] }): ReactElement | null {
  return useRoutes([
    { index: true, element: <IndexScene columns={columns} filterFields={null} /> },
    {
      path: 'create',
      element: (
        <CreateScene initialValues={initialValues}>
          <PriceListForm outlets={outlets} />
        </CreateScene>
      ),
    },
    {
      path: ':id/',
      element: <EntityScene />,
      children: [
        {
          index: true,
          element: (
            <UpdateScene viewDisabled>
              <PriceListForm outlets={outlets} />
            </UpdateScene>
          ),
        },

        {
          path: 'delete',
          element: <DeleteScene />,
        },
      ],
    },
  ]);
}
